import React, { useEffect, useState } from 'react'
import { Alert, CircularProgress, DialogContent, IconButton, Paper, Stack } from '@mui/material'
import { ArrowBack, Download, FactCheck, IosShare } from '@mui/icons-material'
import { observer } from 'mobx-react-lite'
import { Instance } from 'mobx-state-tree'
import { useHistory } from 'react-router-dom'

import { color } from '@trivie/ui-web/theme'
import { Button } from '@trivie/ui-web/components/button'
import { Dialog } from '@trivie/ui-web/components/dialogs/dialog'
import { ConfirmDialog } from '@trivie/ui-web/components/confirm-dialog'
import { isTopic, Question, Topic, useStores } from '@trivie/core'
import { Quiz } from '@trivie/core/src/models/Quiz'
import { AssignmentCreateDialog } from '../../activities/assignments/create-assignment/assignment-create'
import { DateTime } from 'luxon'

import { Text } from '@trivie/ui-web/components/text'
import { QuizCard } from '../quizzes/quiz-card/quiz-card'
import { QuizApi } from '@trivie/core/src/services/api-objects'
import { Path } from '@trivie/core/src/models/workflow/Path'

type ContentCreationHeaderProps = {
  publishData:
    | Instance<typeof Path>
    | Instance<typeof Topic>
    | Instance<typeof Quiz>
    | Instance<typeof Question>
    | null
  showUnsavedPopup?: boolean
  titleHeader: JSX.Element
  onBack: () => void
  onResources?: () => void
  hidePublish?: boolean
  importing?: boolean
  edit?: boolean
  path?: any
  editingPath?: boolean
  save?: boolean
  style?: React.CSSProperties
}

const ContentCreationHeader = observer((props: ContentCreationHeaderProps) => {
  const {
    publishData,
    editingPath,
    hidePublish,
    onBack,
    showUnsavedPopup,
    titleHeader,
    edit,
    save,
    style,
  } = props

  const { contentCreationStore, pathStore, manageUserStore } = useStores()
  const { path } = pathStore
  const { enterpriseTier } = manageUserStore
  const {
    importingQuizzes,
    importingQuestions,
    importedQuestionErrors,
    importedQuizzesErrors,
    quiz,
    resetContentStore,
    selectedTopic,
    topics,
    question,
  } = contentCreationStore

  const [showValidationDialog, setShowValidationDialog] = useState(false)
  const [showUnsavedChanges, setShowUnsavedChanges] = useState(false)
  const [showWizard, setShowWizard] = useState(false)
  const history = useHistory()

  useEffect(() => {
    const { onbeforeunload } = window
    window.onbeforeunload = () => {
      localStorage.setItem('content-temp', JSON.stringify(contentCreationStore))
      return 'dont go'
    }

    return () => {
      window.onbeforeunload = onbeforeunload
    }
  }, [])

  const handleSubmit = async () => {
    if (path) {
      setShowValidationDialog(true)
    } else if (topics?.length) {
      // setValidating(false)
    } else if (quiz) {
      // setValidating(false)
    } else if (question) {
      // setValidating(false)
      setShowValidationDialog(true)
    }
  }

  const handleBack = () => {
    if (showUnsavedPopup) {
      setShowUnsavedChanges(true)
    } else {
      onBack()
    }
  }

  const publishDisabled = () => {
    if (!publishData) {
      return true
    }

    if (path && !path.valid) {
      return true
    } else if (topics && topics?.length > 0) {
      let disabled
      topics &&
        topics?.map((topic) => {
          if (
            (topic.allErrors && topic.allErrors.length > 0) ||
            (topic.quizzes && topic.quizzes?.length === 0) ||
            !topic.title ||
            topic.duplicate_name
          ) {
            disabled = true
          }
        })
      return disabled
    } else if (importingQuestions?.length && importedQuestionErrors?.length) {
      return true
    } else if (importingQuizzes?.length && importedQuizzesErrors?.length) {
      return true
    } else if (
      (quiz && quiz.allErrors && quiz.allErrors.length > 0) ||
      (quiz?.questions && quiz.questions.length === 0)
    ) {
      return true
    } else if (question) {
      return !question.approved
    }
    return false
  }

  const creatingTopic = topics && topics?.length > 0

  const handleClose = () => {
    if (path) {
      history.replace(`/content/paths/${path.path_id}/overview`)
      resetContentStore()
    } else {
      if (topics && topics?.length > 1) {
        history.replace(`/content/topics/`)
      } else if (importingQuizzes?.length) {
        history.replace(`/content/quizzes/`)
      } else if (creatingTopic && topics) {
        history.replace(`/content/topics/${topics[0].topic_id}/overview`)
      } else if (quiz) {
        history.replace(`/content/quizzes/${quiz.quiz_id}/overview`)
      } else if (question) {
        history.replace(`/content/questions/${question.question_id}`)
      }
      resetContentStore()
    }
  }

  return (
    <Paper style={{ ...ROOT, ...style }}>
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
        {!!onBack && (
          <IconButton style={{ left: -20 }} onClick={handleBack} size="large">
            <ArrowBack htmlColor={color.black} />
          </IconButton>
        )}
        {titleHeader}
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 20,
        }}
      >
        {hidePublish ? null : (
          <Button
            disabled={!save && enterpriseTier ? publishDisabled() : quiz?.allErrors.length}
            onClick={() => {
              if (save) {
                handleBack()
              } else {
                localStorage.setItem('skip-tutorial', 'true')
                quiz?.set('touched', true)
                quiz?.questions?.map((q) => {
                  q.set('touched', true)
                  q?.choices.map((c) => c.set('touched', true))
                })
                if (!quiz?.allErrors.length) {
                  handleSubmit()
                  setShowValidationDialog(true)
                }
              }
            }}
            variant="contained"
            color="primary"
            text={save ? 'Save' : 'Publish'}
          />
        )}
      </div>

      {showUnsavedChanges && (
        <ConfirmDialog
          danger
          open={showUnsavedChanges}
          title="Unsaved Changes"
          message={`Your ${
            path ? 'path' : isTopic(publishData) ? 'topic' : 'quiz'
          } has not been published. Are you sure you want to exit without saving?`}
          confirmButton="Discard Changes"
          onConfirm={() => {
            onBack()
            // resetContentStore()
            setShowUnsavedChanges(false)
          }}
          onClose={() => setShowUnsavedChanges(false)}
          handleCancel={() => setShowUnsavedChanges(false)}
        />
      )}

      {showValidationDialog && (
        <PublishDialog
          edit={edit}
          path={path}
          editingPath={editingPath}
          topics={topics}
          setShowWizard={setShowWizard}
          quiz={quiz}
          question={question}
          open={showValidationDialog}
          onConfirm={() => {
            setShowValidationDialog(false)
          }}
          onClose={() => {
            setShowValidationDialog(false)
          }}
        />
      )}

      {showWizard && (
        <AssignmentCreateDialog
          fromContentCreation
          open={showWizard}
          initialPaths={path && path.path_id ? [path.path_id] : []}
          initialTopics={selectedTopic?.topic_id ? [selectedTopic.topic_id] : undefined}
          initialQuizzes={quiz && quiz.quiz_id ? [quiz.quiz_id] : []}
          requestClose={() => {
            setShowWizard(false)
            handleClose()
            resetContentStore()
          }}
        />
      )}
    </Paper>
  )
})

export interface PublishDialogProps {
  open: boolean
  onConfirm: () => void
  onClose: () => void
  quiz?: any
  topics?: any[]
  question?: any
  edit?: boolean
  setShowWizard?: any
  path?: any
  editingPath?: boolean
}

export const PublishDialog = observer((props: PublishDialogProps) => {
  const {
    question,
    path,
    editingPath,
    open,
    edit,
    setShowWizard,
    onConfirm,
    topics,
    onClose,
    quiz,
  } = props

  const { contentCreationStore, pathStore, manageQuizStore, manageUserStore, topicsStore } =
    useStores()
  const { enterpriseTier } = manageUserStore
  const { updatePath, publishPath } = pathStore
  const { exportTopics } = topicsStore
  const { importingQuizzes, importingQuestions, publishContent, isPublishing } =
    contentCreationStore
  const { exportQuizzes } = manageQuizStore
  const [showSettings, setShowSettings] = useState(quiz?.approved ? true : false)
  const [showConfirm, setShowConfirm] = useState(false)

  const [publishData, setPublishData] = useState<any>()

  const history = useHistory()

  const creatingTopic = topics && topics?.length > 0

  useEffect(() => {
    if (
      path ||
      (creatingTopic && topics[0]?.approved && topics?.length === 1) ||
      (quiz && quiz?.approved)
    ) {
      setShowConfirm(true)
      publish()
    }
  }, [])

  const approveAll = () => {
    if (creatingTopic) {
      topics.map((topic) => {
        topic?.quizzes?.map((tq) => {
          tq?.questions.map((qu) => {
            qu?.set('approved', true)
          })
        })
      })
      if (topics?.length === 1) {
        setShowConfirm(true)
        publish()
      }
    } else {
      quiz?.questions.map((qu) => {
        qu?.set('approved', true)
      })
      setShowConfirm(true)
      publish()
    }
  }

  const handleClose = () => {
    // error - content not published close modal or lose progress?
    if (error && !error.contentPublished) {
      // onClose()
      if (topics && topics?.length >= 1) {
        history.replace(`/content/topics`)
      } else if (quiz || importingQuizzes?.length) {
        history.replace(`/content/quizzes`)
      } else if (question || importingQuestions?.length) {
        history.replace(`/content/questions`)
      }
    } else if (path) {
      history.replace(`/content/paths/${path.path_id}/overview`)
      onClose()
    } else {
      if (topics && topics?.length > 1) {
        history.replace(`/content/topics/`)
      } else if (creatingTopic && topics) {
        history.replace(`/content/topics/${topics[0].topic_id}/overview`)
      } else if (quiz && quiz.quiz_id) {
        history.replace(`/content/quizzes/${quiz.quiz_id}/overview`)
      } else if (question && question.question_id) {
        history.replace(`/content/questions/${question.question_id}`)
      } else if (importingQuizzes?.length) {
        history.replace(`/content/quizzes`)
      } else if (importingQuestions?.length) {
        history.replace(`/content/questions`)
      }
      onClose()
    }
  }

  const [error, setError] = useState<any>()
  const publish = () => {
    if (path) {
      if (editingPath) {
        updatePath().then((data: any) => {
          setShowConfirm(true)
        })
      } else {
        publishPath().then((data: any) => {
          setShowConfirm(true)
        })
      }
    } else if (!contentCreationStore.isPublishing) {
      publishContent().then((data) => {
        if (data.error) {
          // if content publish failed - close dialog
          // if only resources or images - show error message, but still redir to topic/quiz/question
          setError(data)
        } else if (data) {
          setPublishData(data)
          // handle cover image updates after ids are in place
          contentCreationStore.setIsPublishingContent(false)
          contentCreationStore.setIsPublishingResources(true)

          // handle resource updates after ids are in place
          contentCreationStore.publishResources().then(() => {
            contentCreationStore.setIsPublishingResources(false)
          })

          contentCreationStore.setIsPublishingImages(true)

          // handle cover image updates after ids are in place
          contentCreationStore.publishImages().then(() => {
            contentCreationStore.setIsPublishingImages(false)
          })

          if (
            question ||
            (topics && topics?.length > 1) ||
            data?.quiz?.length > 1 ||
            data?.question?.length > 1
          ) {
            return
          }
          setShowConfirm(true)
          if (!edit && !isTopic) {
            QuizApi.toggleQuizStatus(data?.quiz[0].quiz_id, 'live')
          }
        }
      })
    }
  }

  const exportData = async () => {
    if (creatingTopic && publishData) {
      const { file_url } = (await exportTopics([publishData?.topic[0].topic_id])) as any
      window.open(file_url)
    } else if (publishData?.quiz[0]) {
      const { file_url } = (await exportQuizzes([publishData.quiz[0]?.quiz_id])) as any
      window.open(file_url)
    }
  }

  const share = () => {
    if (enterpriseTier) {
      setShowWizard(true)
      onClose()
    } else {
      history.replace(`/content/quizzes/${publishData?.quiz[0].quiz_id}/overview/?share=true`)
    }
  }

  return error?.message ? (
    <Dialog
      hideCloseIcon
      onConfirm={handleClose}
      confirmBtnLabel={'Okay'}
      dialogText={error.message}
      title="Error"
      danger
      open={open}
      maxWidth="xs"
      disableBackdropClick
    />
  ) : topics && topics?.length > 1 ? (
    <Dialog
      disableBackdropClick
      onClose={publishData ? handleClose : onClose}
      hideCloseIcon={isPublishing}
      disableConfirm={isPublishing}
      onConfirm={publishData ? handleClose : publish}
      confirmBtnLabel={publishData ? 'Done' : 'Publish Topics'}
      dialogText={
        publishData
          ? 'Your topics have been published successfully.'
          : !isPublishing
            ? 'Are you sure you want to publish these topics?'
            : ''
      }
      title={publishData ? 'Success' : 'Publish'}
      open={open}
      maxWidth="xs"
    >
      {isPublishing && (
        <Stack direction="row" justifyContent="center">
          <CircularProgress />
        </Stack>
      )}
    </Dialog>
  ) : importingQuizzes?.length ? (
    <Dialog
      disableBackdropClick
      onClose={publishData ? handleClose : onClose}
      hideCloseIcon={isPublishing}
      disableConfirm={isPublishing}
      onConfirm={publishData ? handleClose : publish}
      confirmBtnLabel={publishData ? 'Done' : 'Publish'}
      dialogText={
        publishData
          ? 'Your quizzes have been published successfully.'
          : !isPublishing
            ? 'Are you sure you want to publish these quizzes?'
            : ''
      }
      title={publishData ? 'Success' : 'Publish'}
      open={open}
      maxWidth="xs"
    >
      {isPublishing && (
        <Stack direction="row" justifyContent="center">
          <CircularProgress />
        </Stack>
      )}
    </Dialog>
  ) : importingQuestions?.length ? (
    <Dialog
      disableBackdropClick
      onClose={publishData ? handleClose : onClose}
      hideCloseIcon={isPublishing}
      disableConfirm={isPublishing}
      onConfirm={publishData ? handleClose : publish}
      confirmBtnLabel={publishData ? 'Done' : 'Publish'}
      dialogText={
        publishData
          ? 'Your questions have been published successfully.'
          : !isPublishing
            ? 'Are you sure you want to publish these questions?'
            : ''
      }
      title={publishData ? 'Success' : 'Publish'}
      open={open}
      maxWidth="xs"
    >
      {isPublishing && (
        <Stack direction="row" justifyContent="center">
          <CircularProgress />
        </Stack>
      )}
    </Dialog>
  ) : question ? (
    <Dialog
      disableBackdropClick
      onClose={publishData ? handleClose : onClose}
      hideCloseIcon={isPublishing}
      disableConfirm={isPublishing}
      onConfirm={publishData ? handleClose : publish}
      confirmBtnLabel={publishData ? 'Done' : 'Publish'}
      dialogText={
        publishData
          ? 'Your question has been published successfully.'
          : !isPublishing
            ? 'Are you sure you want to publish this question?'
            : ''
      }
      title={publishData ? 'Success' : 'Publish'}
      open={open}
      maxWidth="xs"
    >
      {isPublishing && (
        <DialogContent>
          <CircularProgress />
        </DialogContent>
      )}
    </Dialog>
  ) : (
    <Dialog
      disableBackdropClick
      onClose={handleClose}
      hideCloseIcon={isPublishing}
      maxWidth="xs"
      title={
        showConfirm
          ? path
            ? 'Path Published!'
            : topics && topics?.length > 0
              ? 'Topic Published!'
              : 'Quiz Published!'
          : showSettings
            ? 'Confirm Quiz Settings'
            : topics && topics?.length > 0
              ? 'Quizzes Awaiting Review'
              : 'Questions Awaiting Review'
      }
      aria-labelledby="exit-dialog-title"
      open={open}
      actions={
        !isPublishing && (
          <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end' }}>
            <div style={{ display: 'flex', gap: 12 }}>
              {showConfirm && !path && (
                <Button
                  text="Export"
                  variant="outlined"
                  color="info"
                  startIcon={<Download />}
                  onClick={exportData}
                />
              )}
              {showConfirm &&
                !creatingTopic &&
                !path &&
                !manageUserStore.isSubscriptionWorkspace && (
                  <Button
                    variant={enterpriseTier ? 'outlined' : 'contained'}
                    onClick={share}
                    color={enterpriseTier ? 'primary' : 'info'}
                    startIcon={enterpriseTier ? <FactCheck /> : <IosShare />}
                    text={enterpriseTier ? 'Assign' : 'Share'}
                  />
                )}
              {creatingTopic && showConfirm && !manageUserStore.isSubscriptionWorkspace && (
                <Button
                  variant="outlined"
                  color="info"
                  onClick={() => {
                    onClose()
                    setShowWizard(true)
                  }}
                  startIcon={<FactCheck />}
                  text="Assign Topic"
                />
              )}
              {path && showConfirm && !manageUserStore.isSubscriptionWorkspace && (
                <Button
                  variant="outlined"
                  color="info"
                  onClick={() => {
                    onClose()
                    setShowWizard(true)
                  }}
                  startIcon={<FactCheck />}
                  text="Assign Path"
                />
              )}
              {showConfirm && <Button onClick={handleClose} text="Done" />}
              {!showConfirm && (
                <>
                  <Button variant="outlined" color="info" onClick={onClose} text="Cancel" />
                  <Button
                    disabled={isPublishing}
                    onClick={showSettings ? publish : approveAll}
                    text={showSettings ? 'Publish' : 'Approve All'}
                  />
                </>
              )}
            </div>
          </div>
        )
      }
    >
      {isPublishing ? (
        <Stack direction="row" justifyContent="center">
          <CircularProgress />
        </Stack>
      ) : showConfirm ? (
        <div style={{ flexDirection: 'column', display: 'flex', alignItems: 'center' }}>
          {path ? (
            <QuizCard
              path={path}
              disableHover
              onEdit={() => {}}
              onExport={() => {}}
              onShare={() => {}}
              onDelete={() => {}}
              title={path.name}
              image={path.image}
              questionCount={0}
              lastUpdated={DateTime.now().toFormat('MMM d, yyyy')}
              onClick={() => {}}
              hideMenu
              status={''}
              hideChip
              fullWidth
            />
          ) : creatingTopic && topics && topics?.length > 0 ? (
            <QuizCard
              topic
              disableHover
              onEdit={() => {}}
              onExport={() => {}}
              onShare={() => {}}
              onDelete={() => {}}
              title={topics[0]?.title}
              image={topics[0]?.image}
              questionCount={topics[0]?.quizzes?.length}
              lastUpdated={DateTime.now().toFormat('MMM d, yyyy')}
              onClick={() => {}}
              hideMenu
              status={''}
              hideChip
              fullWidth
            />
          ) : (
            <QuizCard
              disableHover
              onEdit={() => {}}
              onExport={() => {}}
              onShare={() => {}}
              onDelete={() => {}}
              title={quiz?.title}
              image={quiz?.image}
              questionCount={quiz?.questions?.filter((q) => !q.removing).length}
              lastUpdated={DateTime.now().toFormat('MMM d, yyyy')}
              onClick={() => {}}
              hideMenu
              status={'Live'}
              fullWidth
            />
          )}
        </div>
      ) : creatingTopic && topics && topics?.length > 0 ? (
        <Text
          variant="body1Medium"
          text={`${topics[0]?.quizzes?.filter((q) => !q.approved)?.length} quiz${
            topics[0]?.quizzes?.filter((q) => !q.approved)?.length === 1 ? '' : 'zes'
          } ${
            topics[0]?.quizzes?.filter((q) => !q.approved)?.length === 1 ? 'is' : 'are'
          } still awaiting review. Do you want to approve ${
            topics[0]?.quizzes?.filter((q) => !q.approved)?.length === 1 ? 'this' : 'these'
          } ${
            topics[0]?.quizzes?.filter((q) => !q.approved)?.length === 1 ? 'quiz' : 'quizzes'
          } and proceed?`}
          style={{ marginTop: 30, marginBottom: 30, textAlign: 'center' }}
        />
      ) : (
        <Text
          variant="body1Medium"
          text={`${quiz?.questions?.filter((q) => !q.approved && !q.removing)?.length} question${
            quiz?.questions?.filter((q) => !q.approved && !q.removing)?.length === 1 ? '' : 's'
          } ${
            quiz?.questions?.filter((q) => !q.approved && !q.removing)?.length === 1 ? 'is' : 'are'
          } still awaiting review. Do you want to approve ${
            quiz?.questions?.filter((q) => !q.approved && !q.removing)?.length === 1
              ? 'this'
              : 'these'
          } ${
            quiz?.questions?.filter((q) => !q.approved && !q.removing)?.length === 1
              ? 'question'
              : 'questions'
          } and proceed?`}
          style={{ marginTop: 30, marginBottom: 30, textAlign: 'center' }}
        />
      )}
    </Dialog>
  )
})

const ROOT: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  paddingTop: 10,
  paddingBottom: 10,
  paddingRight: 24,
  paddingLeft: 28,
  borderRadius: 0,
  borderBottomWidth: 1,
  boxShadow: '0px -1px 0px 0px #E2E2EA inset',
  alignItems: 'center',
  height: 70,
  zIndex: 1300,
  position: 'relative',
}

export default ContentCreationHeader
